import React from "react";

function IconSpark(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M12 1.5l1.9 5.4 5.6.1-4.5 3.4 1.7 5.5L12 12.9 7.3 16l1.7-5.5-4.5-3.4 5.6-.1L12 1.5z"
      />
    </svg>
  );
}

function IconClipboard(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M9 2h6a2 2 0 0 1 2 2h1a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1a2 2 0 0 1 2-2zm6 2H9v1h6V4zM6 6v14h12V6H6z"
      />
    </svg>
  );
}

function IconStorefront(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M4 7l1-4h14l1 4v3a3 3 0 0 1-2 2.8V21h-5v-5H11v5H6v-8.2A3 3 0 0 1 4 10V7zm2 5.8V19h3v-5h6v5h3v-6.2a3.3 3.3 0 0 1-2.1-.9A3.3 3.3 0 0 1 12 12a3.3 3.3 0 0 1-3.9 0 3.3 3.3 0 0 1-2.1.8zM6 7v3a1.5 1.5 0 0 0 3 0V7H6zm5 0v3a1.5 1.5 0 0 0 3 0V7h-3zm5 0v3a1.5 1.5 0 0 0 3 0V7h-3z"
      />
    </svg>
  );
}

function IconLock(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zM10 7a2 2 0 0 1 4 0v2h-4V7zm2 12a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
      />
    </svg>
  );
}

function IconWarning(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M12 2 1 21h22L12 2zm1 15h-2v2h2v-2zm0-8h-2v6h2V9z"
      />
    </svg>
  );
}

function IconBasket(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M7 9 5 3h2l1.6 6h6.8L17 3h2l-2 6h4v2h-1l-1.4 10.1A2 2 0 0 1 16.6 23H7.4a2 2 0 0 1-1.98-1.9L4 11H3V9h4zm-1 2 1.2 10h9.6L18 11H6zm3 2h2v7H9v-7zm4 0h2v7h-2v-7z"
      />
    </svg>
  );
}

function IconSearchPlus(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M10 2a8 8 0 1 0 4.9 14.3l4.4 4.4 1.4-1.4-4.4-4.4A8 8 0 0 0 10 2zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12zm9 0h2v3h3v2h-3v3h-2V9h-3V7h3V4z"
      />
    </svg>
  );
}

function formatGbp(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "£0.00";
  return `£${n.toFixed(2)}`;
}

function TextRow({ icon: Icon, children }) {
  return (
    <p className="flex items-start gap-2 text-[16px] font-medium leading-snug text-white/95">
      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center text-white/95">
        <Icon className="h-5 w-5" />
      </span>
      <span>{children}</span>
    </p>
  );
}

function SmartBasketBanner({
  className = "",
  variant = "empty", // empty | success | limited | no_matches | locked
  savingsAmount,
  retailerName,
  basketTotal,
  matchedCount,
  totalItemCount,
  retailerCount,
  priceMode,
  lastUpdated,
  supportingCopy,
  onPrimary,
  onSecondary,
  onTertiary,
}) {
  const shellClass =
    "relative w-full overflow-hidden bg-gradient-to-b from-[#7352C7] to-[#A67FFB] rounded-xl border border-white/20 shadow-[0_16px_40px_-20px_rgba(123,94,212,0.55)]";

  const savings = Number(savingsAmount);
  const hasSavings = Number.isFinite(savings) && savings > 0.005;
  const heading = "Today’s Smart Basket";

  const model = (() => {
    if (variant === "success") {
      const primary = hasSavings ? `Save up to ${formatGbp(savings)}` : "Your smartest basket is ready";
      const body = hasSavings
        ? "Compare your full list across supermarkets and unlock the lowest total for your shop today. We’ve matched your items, applied the best available offers, and calculated the smartest basket for you."
        : "We’ve built your best-value basket for today by matching your items, applying the best available offers, and calculating the lowest total across supported retailers.";
      const support = supportingCopy
        || [
          retailerName && Number.isFinite(Number(basketTotal))
            ? `Best basket: ${retailerName} (${formatGbp(basketTotal)})`
            : "",
          typeof matchedCount === "number" && typeof retailerCount === "number"
            ? `Compared items: ${matchedCount} across ${retailerCount} retailers`
            : "",
          priceMode ? `Price mode: ${priceMode}` : "",
          lastUpdated ? `Updated: ${lastUpdated}` : "",
        ].filter(Boolean).join(" • ");

      return {
        iconLeft: IconSpark,
        iconRight: IconStorefront,
        primary,
        body,
        support: support || "Smart Basket is ready.",
        primaryCta: { label: "Review Basket", onClick: onPrimary },
        secondaryCta: { label: "Compare Prices", onClick: onSecondary },
      };
    }

    if (variant === "limited") {
      const support = supportingCopy
        || [
          retailerName && Number.isFinite(Number(basketTotal))
            ? `Current lowest basket found: ${retailerName} (${formatGbp(basketTotal)})`
            : "",
          typeof matchedCount === "number" && typeof totalItemCount === "number"
            ? `Comparable items: ${matchedCount} of ${totalItemCount}`
            : "",
        ].filter(Boolean).join(" • ");

      return {
        iconLeft: IconWarning,
        iconRight: IconStorefront,
        primary: "Your basket is ready — comparison is limited",
        body:
          "We found items in your list, but there are not enough strong common matches across retailers yet to calculate a full like-for-like smart basket.",
        support: support || "Comparable items are limited.",
        helper: "Try adding more everyday branded items or review near matches to improve comparison quality.",
        primaryCta: { label: "Review Basket", onClick: onPrimary },
        secondaryCta: { label: "Compare Prices", onClick: onSecondary },
      };
    }

    if (variant === "no_matches") {
      return {
        iconLeft: IconBasket,
        iconRight: IconSearchPlus,
        primary: "We need at least one matched item to build your basket",
        body:
          "Your active list does not currently contain products we can compare across stores. Add items manually or paste a clearer list to get started.",
        support: supportingCopy || "No matched products yet.",
        primaryCta: { label: "Paste Your List", onClick: onPrimary },
        secondaryCta: { label: "Add Items", onClick: onSecondary },
        tertiary: onTertiary ? { label: "Refresh Matches", onClick: onTertiary } : null,
      };
    }

    if (variant === "locked") {
      return {
        iconLeft: IconLock,
        iconRight: IconSpark,
        primary: "Unlock full Smart Basket insights",
        body:
          "Upgrade to see your best-value basket, savings totals, and advanced comparison tools across supported retailers.",
        support:
          supportingCopy
          || "Includes smart basket totals, savings visibility, budget tracking, and advanced retailer comparison.",
        primaryCta: { label: "Upgrade Now", onClick: onPrimary },
        secondaryCta: { label: "View Plans", onClick: onSecondary },
      };
    }

    // empty
    return {
      iconLeft: IconSpark,
      iconRight: IconClipboard,
      primary: "Build your basket. Compare smarter. Save with confidence.",
      body:
        "Start with a product search or paste a full shopping list to see your best-value basket across supermarkets.",
      support: supportingCopy || "No active grocery list yet.",
      helper: "Search for a product or paste a list to get started.",
      primaryCta: { label: "Paste Your List", onClick: onPrimary },
      secondaryCta: { label: "Compare Prices", onClick: onSecondary },
    };
  })();

  return (
    <div className={`${shellClass} ${className}`}>
      <div className="flex flex-col gap-8 px-5 py-7 sm:px-7 sm:py-8 lg:flex-row lg:items-stretch lg:gap-10 lg:px-9 lg:py-10">
        <div className="min-w-0 max-w-2xl shrink-0 text-white">
          <p className="text-[16px] font-medium text-white">{heading}</p>
          <h2 className="mt-1 text-[26px] font-bold text-white sm:text-[24px] md:text-[28px]">
            {model.primary}
          </h2>
          <p className="mt-3 text-[16px] font-normal leading-relaxed tracking-wide text-white/95 sm:text-[16px]">
            {model.body}
          </p>
        </div>

        <div className="flex min-w-0 flex-col items-stretch gap-5 lg:max-w-md lg:items-start">
          <div className="flex flex-col gap-3 text-left">
            <TextRow icon={model.iconLeft}>{model.support}</TextRow>
            {model.helper ? <TextRow icon={model.iconRight}>{model.helper}</TextRow> : null}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:justify-start">
            {model.primaryCta?.onClick ? (
              <button
                type="button"
                onClick={model.primaryCta.onClick}
                className="inline-flex items-center justify-center rounded-full border-2 border-white bg-transparent px-6 py-1.5 text-[16px] font-bold text-white shadow-sm"
              >
                {model.primaryCta.label}
              </button>
            ) : null}
            {model.secondaryCta?.onClick ? (
              <button
                type="button"
                onClick={model.secondaryCta.onClick}
                className="inline-flex items-center justify-center rounded-full bg-white px-6 py-1.5 text-[16px] font-bold text-[#7b5ed4] shadow-sm"
              >
                {model.secondaryCta.label}
              </button>
            ) : null}
          </div>

          {model.tertiary?.onClick ? (
            <button
              type="button"
              onClick={model.tertiary.onClick}
              className="self-start text-[16px] font-semibold text-white/95 underline decoration-white/40 underline-offset-4 hover:text-white"
            >
              {model.tertiary.label}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SmartBasketBanner;